import TableBasic from "components/table/TableBasic";
import TableCellText from "components/table/TableTextCell";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useToast } from "rc-toastr";
import { RiEdit2Line } from "react-icons/ri";
import { IoTrashBinOutline } from "react-icons/io5";
import { AiFillExclamationCircle } from "react-icons/ai";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { useDisclosure } from "@chakra-ui/hooks";

import Card from "components/card";
import Switch from "components/switch";
import Alert from "components/alert";
import ConfirmButton from "components/confirm";
import { TextInput, TextArea } from "components/models/ui";
import EntradasEditor from "components/models/EntradasEditor";
import FieldConfigEditor from "components/models/FieldConfigEditor";

import { useBetween } from "use-between";
import _loading from "_loading";

const emptyModel = () => ({
  uid: "",
  version: "",
  nombre: "",
  descripcion: "",
  status: "activo",
  entradas: [],
  calibraciones: [],
  campos_configuracion: [],
});

const StatusBadge = ({ status }) => (
  <span
    className={`rounded-full px-3 py-1 text-xs font-bold ${
      status === "activo"
        ? "bg-green-100 text-green-700 dark:bg-green-400/10 dark:text-green-400"
        : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
    }`}
  >
    {status === "activo" ? "Activo" : "Inactivo"}
  </span>
);

const Models = () => {
  const [models, setModels] = useState([]);
  const [data, setData] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalTitle, setModalTitle] = useState("Nuevo Modelo");
  const [activeRow, setActiveRow] = useState(emptyModel());
  const [error, setError] = useState("");
  const { toast } = useToast();

  const { setLoading } = useBetween(_loading);

  const columns = [
    { Header: "UID", accessor: "uid" },
    { Header: "Versión", accessor: "version" },
    { Header: "Nombre", accessor: "nombre" },
    { Header: "Entradas", accessor: "cant_entradas" },
    { Header: "Dispositivos", accessor: "cant_dispositivos" },
    { Header: "Estado", accessor: "status" },
    { Header: "", accessor: "actions" },
  ];

  const handleAdd = () => {
    setActiveRow(emptyModel());
    setError("");
    setModalTitle("Nuevo Modelo");
    onOpen();
  };

  const handleEdit = (item) => {
    setActiveRow(JSON.parse(JSON.stringify(item)));
    setError("");
    setModalTitle(`Editar ${item.nombre}`);
    onOpen();
  };

  const loadData = async () => {
    setLoading(true);
    const response = await axios.get(`/web/models/`);
    setLoading(false);
    if (response.data.success) {
      setModels(response.data.data);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const rows = models.map((item) => ({
      uid: <TableCellText value={item.uid} />,
      version: <TableCellText value={item.version} />,
      nombre: <TableCellText value={item.nombre} />,
      cant_entradas: <TableCellText value={item.cant_entradas} />,
      cant_dispositivos: <TableCellText value={item.cant_dispositivos} />,
      status: <StatusBadge status={item.status} />,
      actions: (
        <p className="flex justify-end">
          <button
            onClick={() => handleEdit(item)}
            className="mr-[10px] rounded-xl border-2 border-gray-300 px-2 py-2 text-base text-xs font-medium text-navy-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 hover:shadow-sm active:translate-y-0 active:bg-gray-300/10 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:border-white/30 dark:hover:bg-white/10 dark:active:bg-white/20"
          >
            <RiEdit2Line />
          </button>
          <ConfirmButton
            message={`¿Seguro deseas eliminar el modelo ${item.nombre}?`}
            onConfirm={() => deleteData(item._id)}
            className="rounded-xl border-2 border-red-300 px-2 py-2 text-base text-xs font-medium text-navy-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-500 hover:text-white hover:shadow-sm active:translate-y-0 active:bg-gray-300/10 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20"
          >
            <IoTrashBinOutline />
          </ConfirmButton>
        </p>
      ),
      key: item._id,
    }));
    setData(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  const deleteData = async (_id) => {
    setLoading(true);
    const response = await axios.delete(`/web/models/${_id}`);
    setLoading(false);
    if (response.data.success) {
      loadData();
    } else {
      toast.error(response.data.message);
    }
  };

  const saveData = async () => {
    setError("");
    if (!activeRow.uid || !activeRow.nombre) {
      setError("El UID y el Nombre son requeridos");
      return;
    }
    setLoading(true);
    let response;
    if (activeRow._id) {
      response = await axios.post("/web/models/", activeRow);
    } else {
      response = await axios.put("/web/models/", activeRow);
    }
    setLoading(false);
    if (response.data.success) {
      onClose();
      loadData();
      toast.success(response.data.message || "Modelo guardado");
    } else {
      setError(response.data.message);
    }
  };

  return (
    <>
      <div className="mt-3 dark:text-white">
        <TableBasic
          columnsData={columns}
          tableData={data}
          title="Modelos"
          addButton={handleAdd}
        />
      </div>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="!z-[1010]"
        scrollBehavior="outside"
        blockScrollOnMount={false}
        motionPreset="slideInBottom"
      >
        <ModalOverlay className="bg-[#000] !opacity-40 backdrop-blur-sm" />
        <ModalContent className="!z-[1002] !m-auto !w-full !max-w-[1120px] !rounded-[20px] !bg-white shadow-2xl dark:!bg-navy-800">
          <ModalBody>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                saveData();
              }}
            >
              <div className="ml-[18px] mr-5 flex h-[60px] w-[100%] flex-row items-center justify-between">
                <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                  {modalTitle}
                </h2>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                  <Switch
                    checked={activeRow.status === "activo"}
                    onChange={(e) =>
                      setActiveRow({
                        ...activeRow,
                        status: e.target.checked ? "activo" : "inactivo",
                      })
                    }
                  />
                  {activeRow.status === "activo" ? "Activo" : "Inactivo"}
                </label>
              </div>

              <div className="mx-5 mb-4 grid grid-cols-1 gap-3 rounded-lg md:grid-cols-4">
                <TextInput
                  label="UID *"
                  value={activeRow.uid}
                  onChange={(e) => setActiveRow({ ...activeRow, uid: e.target.value.toUpperCase() })}
                  placeholder="Ej: HX"
                />
                <TextInput
                  label="Versión"
                  value={activeRow.version}
                  onChange={(e) => setActiveRow({ ...activeRow, version: e.target.value })}
                  placeholder="Ej: 1"
                />
                <div className="md:col-span-2">
                  <TextInput
                    label="Nombre *"
                    value={activeRow.nombre}
                    onChange={(e) => setActiveRow({ ...activeRow, nombre: e.target.value })}
                    placeholder="Ej: Modelo HX"
                  />
                </div>
                <div className="md:col-span-4">
                  <TextArea
                    label="Descripción"
                    value={activeRow.descripcion}
                    onChange={(e) => setActiveRow({ ...activeRow, descripcion: e.target.value })}
                    placeholder="Descripción opcional del modelo"
                    rows={1}
                  />
                </div>
              </div>

              <hr className="mx-5 mb-4 dark:border-white/10" />

              <div className="mx-5 mb-4">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
                  <h3 className="text-base font-bold text-navy-700 dark:text-white">Entradas</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Variables numéricas que el dispositivo reporta (ej: temperatura, pH, caudal).
                  </p>
                </div>
                <EntradasEditor
                  entradas={activeRow.entradas}
                  onChange={(entradas) => setActiveRow({ ...activeRow, entradas })}
                />
              </div>

              <hr className="mx-5 mb-4 dark:border-white/10" />

              <div className="mx-5 mb-4">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
                  <h3 className="text-base font-bold text-navy-700 dark:text-white">
                    Campos de configuración
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ajustes al configurar un dispositivo (ej: WiFi, intervalo de medición, calibración).
                  </p>
                </div>
                <FieldConfigEditor
                  fields={activeRow.campos_configuracion}
                  onChange={(campos_configuracion) =>
                    setActiveRow({ ...activeRow, campos_configuracion })
                  }
                />
              </div>

              <hr className="mx-5 mb-4 dark:border-white/10" />

              <div className="mx-5 mb-4">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
                  <h3 className="text-base font-bold text-navy-700 dark:text-white">Calibraciones</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Puntos de referencia numéricos del modelo (ej: offset, pendiente).
                  </p>
                </div>
                <EntradasEditor
                  entradas={activeRow.calibraciones}
                  onChange={(calibraciones) => setActiveRow({ ...activeRow, calibraciones })}
                  emptyMessage="Sin calibraciones configuradas. Las calibraciones son puntos de referencia numéricos del modelo (ej: offset, pendiente)."
                  namePlaceholder="Ej: Offset"
                  idPlaceholder="offset"
                  addLabel="Agregar calibración"
                  removeTitle="Eliminar calibración"
                />
              </div>

              {error && (
                <div className="mx-5 mb-4">
                  <Alert
                    icon={<AiFillExclamationCircle color="white" />}
                    status="error"
                    title="Error"
                    message={error}
                  />
                </div>
              )}

              <div className="mb-[20px] ml-[18px] mr-[20px] mt-[24px] grid grid-cols-2">
                <div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border-2 border-red-50 p-2 font-medium text-red-500 transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-600 hover:text-white hover:shadow-md active:translate-y-0 active:bg-red-700 dark:bg-red-400 dark:text-white dark:hover:bg-red-300 dark:active:bg-red-200"
                  >
                    Cancelar
                  </button>
                </div>
                <div className="text-end">
                  <button
                    type="submit"
                    className="rounded-lg bg-green-500 p-2 font-medium text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-600 hover:shadow-md active:translate-y-0 active:bg-green-700 dark:bg-green-400 dark:text-white dark:hover:bg-green-300 dark:active:bg-green-200"
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Models;
